/**
 * Type-safe environment variable configuration using Zod validation.
 *
 * This module validates all environment variables at module load time,
 * providing type-safe access with full TypeScript inference. Zod validation
 * ensures build-time safety and eliminates runtime type errors from
 * misconfigured environment variables.
 * @remarks
 * - All client-side variables MUST use the EXPO_PUBLIC_ prefix
 * - Variables are validated regardless of source (.env, EAS, CI/CD)
 * - Add new variables to the schema below, then access via `env.VARIABLE_NAME`
 * @example
 * ```typescript
 * import { env } from "@/lib/env";
 *
 * const apiUrl = env.EXPO_PUBLIC_GRAPHQL_BASE_URL;
 * const isDev = env.EXPO_PUBLIC_ENV === "dev";
 * ```
 * @see .claude/skills/expo-env-config/SKILL.md for patterns and best practices
 * @module
 */
import { z } from "zod";

/**
 * The environments this app can be built for, in the exact spelling every
 * `.env.<mode>` file and every `eas.json` build profile uses.
 */
const APP_ENVIRONMENTS = ["localhost", "dev", "staging", "production"] as const;

/**
 * The resolved application environment. Unlike the raw variable this is never
 * `undefined` — see {@link appEnvironment}.
 */
export type AppEnvironment = (typeof APP_ENVIRONMENTS)[number];

/**
 * The environment an unreadable configuration resolves to (SE-7834).
 *
 * `production` is the most restrictive answer, so every gate written as
 * `!== "production"` (override allowed, debug logging, dev-only surface) closes
 * and every gate written as `=== "production"` (redirect the design gallery to
 * not-found) fires. Choosing anything else means absent configuration *grants*
 * capability, which is the defect this constant exists to prevent.
 */
const FAIL_CLOSED_APP_ENVIRONMENT: AppEnvironment = "production";

/**
 * Fail-closed resolution of `EXPO_PUBLIC_ENV` (SE-7834).
 *
 * The field used to be `z.enum([...]).optional()`, which parsed an unset value
 * clean as `undefined`. Every consumer gating on
 * `env.EXPO_PUBLIC_ENV !== "production"` then evaluated true — because
 * `undefined !== "production"` — so **absent configuration opened each gate**.
 * This is the runtime twin of SE-7806, which stopped an empty
 * `EXPO_PUBLIC_ENV` becoming the deploy stage at build time.
 *
 * Resolution rules, in order:
 *
 * 1. A recognized value (after trimming) is used as-is.
 * 2. Anything else — unset, empty string (what CI emits for a
 *    declared-but-unset variable), whitespace, mis-cased, or a typo — resolves
 *    to {@link FAIL_CLOSED_APP_ENVIRONMENT}.
 *
 * Rule 2 deliberately **does not throw**. `env.ts` is evaluated at module load
 * inside the shipped bundle, so throwing here is a blank app on boot for every
 * user of a misconfigured build — strictly worse than running in the most
 * restricted mode. The unresolved raw value stays readable via
 * {@link RAW_EXPO_PUBLIC_ENV} for the consumers that must distinguish
 * "unrecognized" from "production" (see its docs).
 */
const appEnvironment = z
  .string()
  .optional()
  .transform((raw): AppEnvironment => {
    const candidate = raw?.trim();
    return (
      APP_ENVIRONMENTS.find(known => known === candidate) ??
      FAIL_CLOSED_APP_ENVIRONMENT
    );
  });

/**
 * Environment variable schema.
 * @remarks
 * Add new environment variables here. Use appropriate Zod types:
 * - `z.string()` for required strings
 * - `z.string().optional()` for optional strings
 * - `z.url()` for URL validation
 * - `z.enum([...])` for constrained values
 */
const envSchema = z.object({
  /**
   * Application environment identifier.
   * Determines API endpoints, feature flags, and logging behavior.
   *
   * Always one of the four known environments — never `undefined`. An
   * unreadable configuration resolves to `"production"` so that gates fail
   * closed; see {@link appEnvironment} for the full resolution rules, and
   * {@link RAW_EXPO_PUBLIC_ENV} when you need the unresolved value.
   */
  EXPO_PUBLIC_ENV: appEnvironment,

  /**
   * GraphQL API base URL.
   * Must be a valid URL. Defaults to localhost for development.
   */
  EXPO_PUBLIC_GRAPHQL_BASE_URL: z
    .string()
    .optional()
    .default("http://localhost:3000/graphql"),

  /**
   * Sentry DSN for error tracking.
   * Required in production, optional in other environments.
   */
  EXPO_PUBLIC_SENTRY_DSN: z.string().optional(),

  /**
   * Ask Gemini streaming endpoint (Lambda function URL, Server-Sent Events).
   * Every Ask Gemini feature — chat, player/scouting summaries, match-report
   * summary, voice dictation — dials this one URL. The former API Gateway
   * WebSocket variable was retired in SE-8432; that API no longer exists.
   */
  EXPO_PUBLIC_SSE_ENDPOINT: z.string().optional(),

  /**
   * Default tenant ID for multi-tenant operations.
   * UUID format expected.
   */
  EXPO_PUBLIC_DEFAULT_TENANT_ID: z.string().optional(),

  /**
   * Default organization ID for organization-scoped operations.
   * UUID format expected.
   */
  EXPO_PUBLIC_DEFAULT_ORGANIZATION_ID: z.string().optional(),

  /**
   * Admin organization ID for administrative features.
   * UUID format expected.
   */
  EXPO_PUBLIC_ADMIN_ORG_ID: z.string().optional(),

  /**
   * PostHog API key for analytics and feature flags.
   * When empty, PostHog is disabled (graceful no-op).
   */
  EXPO_PUBLIC_POSTHOG_API_KEY: z.string().optional(),

  /**
   * PostHog host URL for analytics ingestion.
   * Defaults to PostHog US cloud instance.
   */
  EXPO_PUBLIC_POSTHOG_HOST: z
    .string()
    .optional()
    .default("https://us.i.posthog.com"),

  /**
   * Hard opt-out for PostHog capture, independent of the API key.
   * When "true", the SDK is disabled even if a key is present (SE-5223).
   * Set this in CI / automation pipelines (e.g. Playwright web export) to keep
   * dev/QA/E2E traffic out of the shared PostHog project. Defaults to "false".
   */
  EXPO_PUBLIC_POSTHOG_DISABLED: z
    .string()
    .trim()
    .transform(v => v.toLowerCase())
    .refine(v => v === "true" || v === "false", {
      message: "EXPO_PUBLIC_POSTHOG_DISABLED must be 'true' or 'false'",
    })
    .default("false")
    .transform(v => v === "true"),

  /**
   * Build-time gate for the SE-5508 e2e capture escape hatch. Only when
   * "true" does `posthogConfig` honor the pre-boot `e2e-posthog-capture`
   * localStorage marker that lets the telemetry specs opt capture back in on
   * a build that bakes `EXPO_PUBLIC_POSTHOG_DISABLED`. Set exclusively by the
   * CI Playwright web export — never in a shipped `.env.<mode>` — so a real
   * user writing the marker cannot re-enable capture. Defaults to "false".
   */
  EXPO_PUBLIC_POSTHOG_E2E_OPTIN: z
    .string()
    .trim()
    .transform(v => v.toLowerCase())
    .refine(v => v === "true" || v === "false", {
      message: "EXPO_PUBLIC_POSTHOG_E2E_OPTIN must be 'true' or 'false'",
    })
    .default("false")
    .transform(v => v === "true"),

  /**
   * Jam (jam.dev) team id from the workspace SDK settings page.
   * When empty, Jam session capture is disabled (graceful no-op).
   */
  EXPO_PUBLIC_JAM_TEAM_ID: z.string().optional(),
});

/**
 * Parses and validates environment variables with helpful error messages.
 * @remarks
 * Expo requires static references to process.env.EXPO_PUBLIC_* for values
 * to be inlined at build time. Passing process.env as a whole object does
 * not work - each variable must be explicitly referenced.
 * @returns Validated environment configuration
 * @throws Error with formatted message listing all validation failures
 */
function parseEnv(): z.infer<typeof envSchema> {
  // Must use static references for Expo to inline values at build time
  // See: https://docs.expo.dev/guides/environment-variables/
  const envValues = {
    EXPO_PUBLIC_ENV: process.env.EXPO_PUBLIC_ENV,
    EXPO_PUBLIC_GRAPHQL_BASE_URL: process.env.EXPO_PUBLIC_GRAPHQL_BASE_URL,
    EXPO_PUBLIC_SENTRY_DSN: process.env.EXPO_PUBLIC_SENTRY_DSN,
    EXPO_PUBLIC_SSE_ENDPOINT: process.env.EXPO_PUBLIC_SSE_ENDPOINT,
    EXPO_PUBLIC_DEFAULT_TENANT_ID: process.env.EXPO_PUBLIC_DEFAULT_TENANT_ID,
    EXPO_PUBLIC_DEFAULT_ORGANIZATION_ID:
      process.env.EXPO_PUBLIC_DEFAULT_ORGANIZATION_ID,
    EXPO_PUBLIC_ADMIN_ORG_ID: process.env.EXPO_PUBLIC_ADMIN_ORG_ID,
    EXPO_PUBLIC_POSTHOG_API_KEY: process.env.EXPO_PUBLIC_POSTHOG_API_KEY,
    EXPO_PUBLIC_POSTHOG_HOST: process.env.EXPO_PUBLIC_POSTHOG_HOST,
    EXPO_PUBLIC_POSTHOG_DISABLED: process.env.EXPO_PUBLIC_POSTHOG_DISABLED,
    EXPO_PUBLIC_POSTHOG_E2E_OPTIN: process.env.EXPO_PUBLIC_POSTHOG_E2E_OPTIN,
    EXPO_PUBLIC_JAM_TEAM_ID: process.env.EXPO_PUBLIC_JAM_TEAM_ID,
  };

  const result = envSchema.safeParse(envValues);

  if (!result.success) {
    const formatted = result.error.issues
      .map(issue => `  - ${issue.path.join(".")}: ${issue.message}`)
      .join("\n");

    throw new Error(
      `Environment validation failed:\n${formatted}\n\nEnsure all required EXPO_PUBLIC_* variables are set.`
    );
  }

  return result.data;
}

/**
 * Validated environment configuration.
 * @remarks
 * Access environment variables through this object for type safety.
 * Validation occurs at module load time - if this module loads,
 * all variables are guaranteed to be valid.
 * @example
 * ```typescript
 * import { env } from "@/lib/env";
 *
 * if (env.EXPO_PUBLIC_ENV === "production") {
 *   // Production-specific logic
 * }
 * ```
 */
export const env = parseEnv();

/**
 * The **unresolved** `EXPO_PUBLIC_ENV` as the build baked it, with surrounding
 * whitespace trimmed off — `undefined` when the variable was never set, `""`
 * when it was declared but left empty (or held nothing but whitespace), and
 * whatever string was typed when it is unrecognized.
 *
 * The trim is deliberate and it is the *only* normalization applied. Every
 * consumer below compares this value against the same four recognized
 * spellings that {@link appEnvironment} matches, and `appEnvironment` trims
 * before it matches. Leaving the whitespace on here would split the two: a
 * build baked with `EXPO_PUBLIC_ENV=" production "` would resolve to
 * `"production"` for gates while every consumer of this constant read it as
 * unrecognized — Sentry silently off and tagged `development` on a real
 * production build, Jam off, and analytics filed under a `" production "`
 * label distinct from `"production"`. Before SE-7834 that input could not
 * reach any of them: the schema was `z.enum([...])`, so a padded value threw
 * at module load. Fail-closed resolution replaced that hard stop, and without
 * the trim it would have replaced it with a silent one.
 *
 * Trimming does **not** narrow the unrecognized set that the recorders depend
 * on. `" qa "` trims to `"qa"` and stays unrecognized; `"   "` trims to `""`
 * and stays falsy. Only a *recognized* spelling wearing stray whitespace
 * changes meaning, which is the defect being fixed.
 *
 * `env.EXPO_PUBLIC_ENV` answers "what may this build be allowed to do?" and
 * fails closed to `"production"`. This constant answers the different question
 * "what did the build actually say?", and exists for the consumers whose own
 * safe direction is the opposite one:
 *
 * - `providers/sentry` and `providers/jam` gate capture OFF for any
 *   unrecognized environment, so that a misconfigured build never starts
 *   reporting errors or recording sessions into the production dashboards. For
 *   a recorder, fail-closed means *do not collect* — the opposite of what
 *   resolving to `"production"` would mean.
 * - The analytics sender and identity hook tag events with the environment;
 *   resolving an unset build to `"production"` there would file local and CI
 *   traffic under production analytics.
 * - Surfaces that merely *display* the environment (the sign-in version label,
 *   the settings debug string) show nothing rather than claiming "production".
 *
 * Reach for `env.EXPO_PUBLIC_ENV` for anything that gates behavior. Reach for
 * this only when "unrecognized" is genuinely a distinct case for you. (SE-7834)
 *
 * Declared as a static `process.env.*` reference so Expo inlines it at build
 * time, exactly like the schema fields above; the trim then runs against the
 * inlined literal.
 */
export const RAW_EXPO_PUBLIC_ENV: string | undefined =
  process.env.EXPO_PUBLIC_ENV?.trim();

/**
 * Type representing the validated environment configuration.
 * Use this when typing functions that accept env config.
 */
export type Env = z.infer<typeof envSchema>;
